import type { TranslationCatalog } from "../types.js";
import { pluralBcs } from "../plural.js";

function coinWord(n: number): string {
  return pluralBcs(n, ["novčić", "novčića", "novčića"]);
}

export default {
  "common.loading": "Povezivanje...",
  "common.loadingEllipsis": "Učitavanje...",
  "common.unknownError": "Nepoznata greška",
  "common.errorPrefix": (p: { message: string }) => `Greška: ${p.message}`,
  "common.retry": "Pokušaj ponovo",
  "common.close": "Zatvori",
  "common.back": "← Nazad",
  "common.cancel": "Otkaži",
  "common.details": "Detalji",
  "common.settingsTitle": "⚙️ Podešavanja",
  "common.profile": "👤 Profil",
  "common.rules": "📖 Pravila",

  "login.subtitle": "Prijavi se da igraš.",
  "login.continueFacebook": "Nastavi sa Facebook nalogom",
  "login.continueGoogle": "Nastavi sa Google nalogom",
  "login.continueEmail": "Nastavi sa email adresom",
  "login.tagline": "Adutska kartaška igra s licitacijom za tri igrača. Licitiraj, izaberi aduta i odnesi štihove — prvi do 101 boda pobjeđuje.",
  "login.linkRules": "Pravila",
  "login.linkStrategy": "Strategija",
  "login.linkAbout": "O igri",
  "login.linkPrivacy": "Privatnost",

  "lobby.entryFee": (p: { fee: number }) => `Ulaznica po partiji: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Nastavi partiju",
  "lobby.playButton": "Igraj (3 igrača)",
  "lobby.inviteFriend": "📣 Pozovi prijatelja",
  "lobby.insufficientFunds": (p: { missing: number }) => `Nemaš dovoljno novčića za ulaznicu (nedostaje ${p.missing}).`,
  "lobby.watchAd": "Gledaj reklamu za dodatne novčiće",

  "leaderboard.title": "🏆 Rang lista",
  "leaderboard.emptySeason": "Još nema rangiranih igrača ove sezone (potrebno je odigrati bar 1 partiju).",
  "leaderboard.emptyGlobal": "Još nema rangiranih igrača (potrebno je odigrati bar 1 partiju ukupno).",
  "leaderboard.seasonTabDefault": "Sezona",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Sezona ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Ukupno (sve sezone)",
  "leaderboard.colPlayer": "Igrač",
  "leaderboard.colPoints": "Bodovi",
  "leaderboard.colMatches": "Partije",
  "leaderboard.you": "Ti",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Tvoj skor: ${p.points} bodova iz ${p.matchesPlayed} partija (treba bar 1 partiju za rang)`,

  "rules.title": "📖 Kako se igra Meksiko",
  "rules.cardsHeading": "Karte",
  "rules.cardsBody":
    "Igra se sa 32 karte (7, 8, 9, 10, J, Q, K, A u sve 4 boje). Jačina karata unutar boje, od najslabije: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Adutska boja nosi sve neadutske karte, bez obzira na rang.",
  "rules.dealingHeading": "Dijeljenje",
  "rules.dealingBody": 'Svaki od 3 igrača dobija 10 karata (u dva kruga po 5), a 2 karte ostaju skrivene u "kupu".',
  "rules.biddingHeading": "Licitacija",
  "rules.biddingBody":
    'Prvi igrač mora licitirati bar 5. Svaki sljedeći mora licitirati strogo više (5, 6, 7, 8, 9, 10, ili "MEKSIKO") ili reći "dalje" — ko jednom kaže "dalje", trajno ispada iz licitacije za to dijeljenje. Licitacija se završava kad ostane samo jedan igrač.',
  "rules.talonHeading": "Kup i škart",
  "rules.talonBody":
    "Pobjednik licitacije otvara kup (2 karte, vidljive svima), dodaje ih svojoj ruci (sad ima 12 karata), pa škartira 2 karte nazad licem na dolje. Zatim bira adutsku boju.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Ko licitira "MEKSIKO", obavezuje se da odnese svih 10 štihova. Igra se bez aduta i bez kupa — samo sa originalnih 10 karata.',
  "rules.tricksHeading": "Tok igre (štihovi)",
  "rules.tricksBody":
    "Mora se pratiti iznesena boja ako je imaš. Ako je nemaš, moraš baciti aduta ako ga imaš (sječenje) — tek ako nemaš ni jedno ni drugo, možeš baciti bilo šta. Štih nosi najjača karta iznesene boje, osim ako je neko bacio aduta — tada najjači adut nosi, bez obzira na boju. Ko odnese štih, vodi u sljedećem.",
  "rules.scoringHeading": "Bodovanje",
  "rules.scoringBody":
    "Pobjednik licitacije: ako odnese bar onoliko štihova koliko je licitirao, dobija onoliko poena koliko je stvarno odnio. Ako ne uspije, dobija <strong>negativne</strong> poene u visini licitacije. Ostala dva igrača uvijek dobijaju poene jednake broju štihova koje su odnijeli.",
  "rules.matchEndHeading": "Kraj partije",
  "rules.matchEndBody":
    "Partija se završava kad neko dostigne ciljni skor. Ako više igrača pređe cilj u istom dijeljenju sa istim najvišim skorom, igra se dodatno dijeljenje dok se ne razriješi.",

  "settings.soundOff": "🔇 Zvuk isključen",
  "settings.soundOn": "🔊 Zvuk uključen",
  "settings.displayNameLabel": "Ime u igri",
  "settings.saveDisplayName": "Sačuvaj ime",
  "settings.displayNameLengthError": "Ime mora imati 1-20 karaktera.",
  "settings.saveFailed": "Čuvanje nije uspjelo, pokušaj ponovo.",
  "settings.logout": "🚪 Odjava",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Čekanje igrača... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `Ostalo ${p.remainingSec}s prije nego što AI popuni prazna mjesta`,

  "game.passLabel": "dalje",
  "game.passButton": "Dalje",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Licitacija: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Adut: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Štihova: ${p.count}`,
  "game.biddingInProgress": "Licitacija u toku...",
  "game.yourBid": "Tvoja licitacija",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} škartira...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} bira adut...`,
  "game.chooseTrumpPrompt": "Izaberi adut",
  "game.nextDealPause": "Sljedeće dijeljenje...",
  "game.fromTalon": "iz kupa",
  "game.talonRevealLabel": (p: { bidder: string }) => `Karte iz kupa (${p.bidder})`,
  "game.yourTurnLabel": "Tvoj potez",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 Nisi na vrijeme odigrao/la potez, pa AI trenutno igra umjesto tebe. Klikni bilo gdje da preuzmeš kontrolu.",
  "game.leaveMatch": "Napusti partiju",
  "game.cardAlt": "karta",
  "game.botDisplayName": "AI igrač",
  "game.botBadgeIdle": "🤖 AI (privremeno)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Vodi licitaciju",

  "matchEnd.title": "Partija završena",
  "matchEnd.winner": (p: { name: string }) => `Pobjednik: ${p.name}`,
  "matchEnd.backToLobby": "Nazad u lobi",

  "reactions.sendButton": "Pošalji reakciju",
  "reaction.angry_small_card": "Zašto malom kartom?",
  "reaction.angry_big_card": "Zašto velikom kartom?",
  "reaction.bravo": "Bravo",
  "reaction.svaka_cast": "Svaka čast",
  "reaction.cestitam_pobeda": "Čestitam na pobjedi",
  "reaction.izvini": "Izvini",
  "reaction.pa_zasto": "Pa zašto!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Sezona",
  "medal.season_silver": "🥈 Sezona",
  "medal.season_bronze": "🥉 Sezona",
  "medal.first_match": "🎉 Prva partija",
  "medal.matches_100": "💯 100 partija",
  "medal.mexico_master": "🌵 Meksiko majstor",
  "medal.hat_trick_3_wins": "🔥 Hetrik (3 pobjede)",
  "medal.tricks_1000": "🃏 1000 štihova",

  "suit.diamonds": "Karo",
  "suit.hearts": "Srce",
  "suit.clubs": "Tref",
  "suit.leaves": "List",

  "profile.matchesPlayed": "Odigranih partija",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1. mjesto (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2. mjesto (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3. mjesto (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Bodovanje po partiji: 1. mjesto ${p.win}, 2. mjesto ${p.second}, 3. mjesto ${p.third} bodova`,
  "profile.noMedals": "Još nema osvojenih medalja.",
  "profile.medalsHeading": "Medalje",
  "profile.seasonHistoryHeading": "Historija sezona",
  "profile.colSeason": "Sezona",
  "profile.colRank": "Rang",
  "profile.deleteAccountButton": "🗑️ Obriši nalog",
  "profile.deleteAccountTitle": "Obriši nalog",
  "profile.deleteAccountWarning":
    "Ovo trajno briše tvoj nalog i sve podatke (historiju partija, rang listu, medalje, stanje novčića). Ova akcija se ne može poništiti.",
  "profile.deleteAccountConfirm": "Da, obriši nalog",
  "profile.deleteAccountFailed": "Brisanje nije uspjelo, pokušaj ponovo.",
  "profile.deleteAccountActiveMatch": "Ne možeš obrisati nalog dok si u aktivnoj partiji. Završi ili napusti partiju pa pokušaj ponovo.",

  "errors.NOT_YOUR_TURN": "Nije tvoj red.",
  "errors.INVALID_BID": "Nevažeća licitacija.",
  "errors.BID_TOO_LOW": "Licitacija mora biti veća od trenutno najviše.",
  "errors.MUST_BID_FIRST": "Prvi igrač mora licitirati bar 5, ne može reći dalje.",
  "errors.DUPLICATE_DISCARD": "Ne možeš odbaciti istu kartu dva puta.",
  "errors.CARD_NOT_IN_HAND": "Ta karta nije u tvojoj ruci.",
  "errors.MUST_FOLLOW_SUIT": "Moraš pratiti boju, ili sjeći adutom ako ne možeš da pratiš.",
  "errors.TRICK_INCOMPLETE": "Štih još nije završen.",
  "errors.MATCH_ENDED": "Partija je već završena.",
  "errors.UNKNOWN_ACTION": "Nepoznata akcija.",
  "errors.WRONG_PHASE": "Ta akcija nije dozvoljena u ovoj fazi igre.",
  "errors.NOT_BID_WINNER": "Samo pobjednik licitacije može to da uradi.",
  "errors.MATCH_NOT_ENDED": "Partija još nije završena.",
  "errors.invalid_session_token": "Sesija je istekla, prijavi se ponovo.",
  "errors.room_full": "Sto je popunjen, pokušaj ponovo za trenutak.",
  "errors.insufficient_balance": "Nemaš dovoljno novčića za ulaznicu.",
  "errors.ad_unavailable": "Reklama trenutno nije dostupna. Provjeri internet konekciju (ili isključi AdBlock) i pokušaj ponovo.",
  "errors.invalid_or_early_ticket": "Reklama nije prepoznata kao odgledana. Pokušaj ponovo.",
  "errors.daily_ad_limit_reached": "Dostigao si dnevni limit reklama. Pokušaj sutra.",

  "share.inviteText": "Igraj Meksiko sa mnom!",
  "ad.placeholderText": "Simulirana reklama (standalone web demo)",
  "rotateOverlay.text": "Okreni telefon da igraš Meksiko",
  "languageSwitcher.ariaLabel": "Jezik",
} satisfies TranslationCatalog;
